import { ReactNode, useContext } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Notification,
  Link,
  SidebarItem,
  SidebarItemIcon,
  SidebarItemLabel,
  useResponsive,
  Divider,
  useDisclosure,
} from '@bitrise/bitkit';
import PageLayout from '@/components/page/PageLayout/PageLayout';
import { getGitProviderIconName } from '@/models/GitProvider';
import {
  appSettingsPath,
  installableArtifactsPath,
  basePathForApp,
  billingPath,
  dashboardPath,
  insightsExploreBuildsPath,
  workflowEditorPath,
  buildCacheInvocationListPath,
} from '@/utils/monolithApiRouteService';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { PageLayoutSidebarItem } from '@/components/page/PageLayout/PageLayoutSidebar';
import HighlightTips from '@/components/HighlightTips/HighlightTips';
import WatchAppToggle from '../AppDetails/WatchAppToggle';
import AppTitle from '../AppDetails/components/AppTitle/AppTitle';
import AppDetailsPageProps from '../types/AppDetailsPageProps';
import useWorkflowInfoHighlight from '../AppDetails/hooks/useWorkflowInfoHighlight';
import AppPageProps from '../types/AppPageProps';
import { ScheduledBuild } from '../AppDetails/components/ScheduledBuilds/scheduledBuilds.types';
import StartBuildDialog from './StartBuildDialog/StartBuildDialog';
import ScheduleBuildDialog from './ScheduleBuildDialog/ScheduleBuildDialog';

function getSideBarItems(appSlug: string, isViewedAsPublicPage: boolean): PageLayoutSidebarItem[] {
  const items: PageLayoutSidebarItem[] = [
    {
      itemProps: {
        href: basePathForApp(appSlug),
      },
      iconProps: {
        name: 'Build',
      },
      label: 'Builds',
      pageName: 'Builds',
    },
  ];

  if (!isViewedAsPublicPage) {
    items.push({
      itemProps: {
        href: installableArtifactsPath(appSlug),
      },
      iconProps: {
        name: 'Download',
      },
      label: 'Artifacts',
      pageName: 'Artifacts',
    });
  }

  return items;
}

type AppPageLayoutProps = AppPageProps & {
  abilities: AppDetailsPageProps['abilities'];
  app: AppDetailsPageProps['app'];
  children: ReactNode;
  currentPage: 'Builds' | 'Artifacts';
  isViewedAsPublicPage: boolean;
  isWatchingApp: boolean;
  onStartOrScheduleBuildDialogClose?(): void;
  scheduledBuild?: ScheduledBuild;
  workflowsInfoShown: boolean;
};

export const AppPageLayout = ({
  abilities,
  app,
  children,
  currentPage,
  isOwnerUser,
  isViewedAsPublicPage,
  isWatchingApp,
  onStartOrScheduleBuildDialogClose,
  scheduledBuild,
  shouldShowFailedPaymentNotification,
  workflowsInfoShown,
}: AppPageLayoutProps) => {
  const { account } = useContext(GlobalPropsContext);
  const { isMobile } = useResponsive();

  const { isWorkflowInfoVisible, onWorkflowInfoClose } = useWorkflowInfoHighlight(
    workflowsInfoShown,
    currentPage,
    app.hasAnyBuild,
  );
  const {
    isOpen: isStartBuildDialogOpen,
    onClose: onStartBuildDialogClose,
    onOpen: onStartBuildDialogOpen,
  } = useDisclosure();

  const {
    isOpen: isScheduleBuildDialogOpen,
    onClose: onScheduleBuildDialogClose,
    onOpen: onScheduleBuildDialogOpen,
  } = useDisclosure();

  const handleStartBuildDialogClose = () => {
    onStartBuildDialogClose();
    onStartOrScheduleBuildDialogClose?.();
  };

  const handleScheduleBuildDialogClose = () => {
    onScheduleBuildDialogClose();
    onStartOrScheduleBuildDialogClose?.();
  };

  const notifications = (shouldShowFailedPaymentNotification || app.isDisabled) && (
    <Box display="flex" flexDir="column" gap="16" marginBottom="16">
      {shouldShowFailedPaymentNotification && (
        <Notification status="error">
          Payment for this {app.ownerType === 'Organization' ? 'workspace' : "project's workspace"} is now overdue. To
          ensure continued access to Bitrise,{' '}
          {isOwnerUser ? (
            <>
              please review your{' '}
              <Link href={billingPath(app.ownerType, app.owner.slug)} isExternal isUnderlined>
                billing information.
              </Link>
            </>
          ) : (
            `please contact ${
              app.ownerType === 'Organization' ? app.owner.name : app.owner.username
            } to review the billing information.`
          )}
        </Notification>
      )}
      {app.isDisabled && (
        <Notification status="error">
          This project has been disabled. You can re-enable it on the{' '}
          <Link href={appSettingsPath(app.slug, 'builds')} isUnderlined>
            Builds
          </Link>{' '}
          tab of the app settings to start building again.
        </Notification>
      )}
    </Box>
  );

  const showAppSettingsButton = abilities.canChangeSettings || abilities.canAccessBaseTeamTab;
  const appSettingsPagePath = abilities.canChangeSettings
    ? appSettingsPath(app.slug, 'general')
    : appSettingsPath(app.slug, 'team');

  const workflowButton = (isSticky: boolean) =>
    abilities.canChangeSettings && (
      <HighlightTips
        visible={isWorkflowInfoVisible && !isMobile}
        onClose={onWorkflowInfoClose}
        title="Explore Workflows & Pipelines"
        message="Set up your Workflows or use the default ones to start running builds."
      >
        <Button
          variant="secondary"
          as="a"
          href={workflowEditorPath(app.slug)}
          size={isSticky ? 'sm' : 'md'}
          leftIconName="Workflow"
          data-intercom-target="Workflows button"
        >
          Workflows
        </Button>
      </HighlightTips>
    );

  const startBuildButton = (isSticky: boolean) =>
    abilities.canRunBuilds && (
      <ButtonGroup isAttached>
        <Button size={isSticky ? 'sm' : 'md'} flex="1" leftIconName="Play" onClick={onStartBuildDialogOpen}>
          Start build
        </Button>
        <IconButton
          iconName="Clock"
          aria-label="Schedule builds"
          onClick={onScheduleBuildDialogOpen}
          size={isSticky ? 'sm' : 'md'}
        />
      </ButtonGroup>
    );

  const buttons = (isSticky: boolean) => (
    <>
      {showAppSettingsButton && !isMobile && (
        <Button
          aria-label="Project Settings"
          as="a"
          href={appSettingsPagePath}
          leftIconName="Settings"
          size={isSticky ? 'sm' : 'md'}
          variant="secondary"
        >
          Project settings
        </Button>
      )}
      {isMobile ? (
        <Box display="flex" gap="12" css={{ '& > *': { flex: 1 } }}>
          {workflowButton(isSticky)}
          {startBuildButton(isSticky)}
        </Box>
      ) : (
        <>
          {workflowButton(isSticky)}
          {startBuildButton(isSticky)}
        </>
      )}
    </>
  );

  return (
    <PageLayout
      footerBackground={currentPage === 'Builds' ? 'neutral.100' : undefined}
      header={{
        breadcrumbData: [
          {
            key: '1',
            children: 'Bitrise CI',
            href: dashboardPath(),
          },
        ],
        children: ({ isSticky }) => (isViewedAsPublicPage ? null : buttons(isSticky)),
        extraContent: notifications,
        pageTitleContent: (
          <AppTitle
            app={app}
            appSettingsPagePath={appSettingsPagePath}
            showAppSettingsButton={isMobile && showAppSettingsButton}
          />
        ),
        pageTitleString: app.title,
      }}
      isMobile={isMobile}
      platformShell={
        isViewedAsPublicPage
          ? {
              navWidth: 0,
              headerHeight: 16,
            }
          : undefined
      }
      sidebar={{
        items: getSideBarItems(app.slug, isViewedAsPublicPage),
        extraContent: !isViewedAsPublicPage && abilities.canWatch && (
          <WatchAppToggle appSlug={app.slug} isInitialWatching={isWatchingApp} />
        ),
        selectedPage: currentPage,
        footer: (
          <>
            <Divider marginTop={[16, 12]} marginY={[16, 24]} marginX={[32, 0]} w="auto" />
            {account && !isViewedAsPublicPage && (
              <SidebarItem
                href={buildCacheInvocationListPath(account.slug, {
                  params: { project_slug: app.slug },
                })}
                isExternal
              >
                <SidebarItemIcon name="BuildCache" />
                <SidebarItemLabel>Build Cache</SidebarItemLabel>
                <SidebarItemIcon color="icon/tertiary" name="ArrowNorthEast" />
              </SidebarItem>
            )}
            {account && !isViewedAsPublicPage && (
              <SidebarItem
                href={insightsExploreBuildsPath(account.slug, {
                  params: { ref: 'subheader-cta', app_slug: app.slug },
                })}
                isExternal
              >
                <SidebarItemIcon name="Insights" />
                <SidebarItemLabel>Insights</SidebarItemLabel>
                <SidebarItemIcon color="icon/tertiary" name="ArrowNorthEast" />
              </SidebarItem>
            )}
            <SidebarItem href={app.repositoryUrl} isExternal>
              <SidebarItemIcon name={getGitProviderIconName(app.provider)} />
              <SidebarItemLabel>Source</SidebarItemLabel>
              <SidebarItemIcon color="icon/tertiary" name="OpenInBrowser" />
            </SidebarItem>
          </>
        ),
      }}
    >
      {children}
      {!isViewedAsPublicPage && (
        <>
          <StartBuildDialog
            app={app}
            buildTriggerToken={app.buildTriggerToken}
            defaultBranch={app.defaultBranch}
            isOpen={isStartBuildDialogOpen}
            onClose={handleStartBuildDialogClose}
            openScheduleBuildDialog={onScheduleBuildDialogOpen}
          />
          <ScheduleBuildDialog
            app={app}
            buildTriggerToken={app.buildTriggerToken}
            defaultBranch={app.defaultBranch}
            isOpen={isScheduleBuildDialogOpen || Boolean(scheduledBuild)}
            onClose={handleScheduleBuildDialogClose}
            scheduledBuild={scheduledBuild}
          />
        </>
      )}
    </PageLayout>
  );
};
